import { TAccount, TAccountType, TCurrency, TTransaction } from "@/api/v1_0";
import { JSONSchema, validateData } from "@/validation";
import {
  Min,
  Number,
  Optional,
  Required,
  Rules,
  String,
  Uuid,
} from "@/validation/rules";

export namespace Transaction {
  export interface IInput extends Partial<TTransaction> {
    uuid?: string;
    leftType?: TAccountType;
    leftUuid?: string;
    leftCurrency?: TCurrency;
    rightType?: TAccountType;
    rightUuid?: string;
    rightCurrency?: TCurrency;
    leftAmount?: number;
    rate?: number;
    rightAmount?: number;
    /**
     * @format yyyy-MM-dd H:i
     * @example 2020-03-15 11:20
     */
    processedAt?: string;
    description?: string;
  }

  export interface IErrors {
    uuid?: string;
    leftType?: string;
    leftUuid?: string;
    leftCurrency?: string;
    rightType?: string;
    rightUuid?: string;
    rightCurrency?: string;
    leftAmount?: string;
    rate?: string;
    rightAmount?: string;
    processedAt?: string;
    description?: string;
  }

  export const create = (
    leftAccount?: TAccount,
    rightAccount?: TAccount
  ): IInput => ({
    leftType: leftAccount?.type,
    leftUuid: leftAccount?.uuid,
    leftCurrency: leftAccount?.currency,
    rightType: rightAccount?.type,
    rightUuid: rightAccount?.uuid,
    rightCurrency: rightAccount?.currency,
  });

  const validationRules: JSONSchema<IInput> = {
    type: "object",
    properties: {
      uuid: Rules(Optional(), Uuid()),
      leftType: Rules(Required(), String()),
      leftUuid: Rules(Required(), Uuid()),
      leftCurrency: Rules(Required()),
      rightType: Rules(Required(), String()),
      rightUuid: Rules(Required(), Uuid()),
      rightCurrency: Rules(Required()),
      leftAmount: Rules(Required(), Number(), Min(1)),
      rate: Rules(Optional(), Number(), Min(0)),
      rightAmount: Rules(Optional(), Number(), Min(1)),
      processedAt: Rules(Optional()),
      description: Rules(Optional()),
      operationName: Rules(Optional()),
    },
  };

  export const validate = (
    input: IInput
  ):
    | { failed: false; values: TTransaction }
    | { failed: true; errors: IErrors } => {
    const v = validateData<IInput, IErrors, TTransaction>(
      input,
      validationRules
    );

    if (v.failed) {
      return v;
    }

    if (input.leftCurrency !== input.rightCurrency && !input.rightAmount) {
      return {
        failed: true,
        errors: {
          rightAmount: "Souldn't be null",
        },
      };
    }

    return v;
  };

  export const allowLeftTypes = (
    rightType: TAccountType | undefined
  ): Array<TAccountType> => {
    if (!rightType) {
      return [
        TAccountType.IncomeSource,
        TAccountType.Debt,
        TAccountType.Goal,
        TAccountType.Saving,
      ];
    }

    switch (rightType) {
      case TAccountType.Debt:
        return [
          TAccountType.IncomeSource,
          TAccountType.Goal,
          TAccountType.Saving,
        ];
      case TAccountType.ExpenseCategory:
        return [TAccountType.Debt, TAccountType.Goal, TAccountType.Saving];
      case TAccountType.Goal:
        return [
          TAccountType.IncomeSource,
          TAccountType.Goal,
          TAccountType.Saving,
        ];
      case TAccountType.IncomeSource:
        return [];
      case TAccountType.Saving:
        return [
          TAccountType.IncomeSource,
          TAccountType.Goal,
          TAccountType.Saving,
        ];
    }
  };

  export const allowRightTypes = (
    leftType: TAccountType | undefined
  ): Array<TAccountType> => {
    if (!leftType) {
      return [
        TAccountType.ExpenseCategory,
        TAccountType.Debt,
        TAccountType.Goal,
        TAccountType.Saving,
      ];
    }

    switch (leftType) {
      case TAccountType.Debt:
        return [TAccountType.Goal, TAccountType.Saving];
      case TAccountType.ExpenseCategory:
        return [];
      case TAccountType.Goal:
        return [
          TAccountType.ExpenseCategory,
          TAccountType.Debt,
          TAccountType.Goal,
          TAccountType.Saving,
        ];
      case TAccountType.IncomeSource:
        return [TAccountType.Goal, TAccountType.Debt, TAccountType.Saving];
      case TAccountType.Saving:
        return [
          TAccountType.ExpenseCategory,
          TAccountType.Debt,
          TAccountType.Goal,
          TAccountType.Saving,
        ];
    }
  };
}
